import React from "react";
import { trackEvent } from "~/utils/analytics";

export type Term = { term: string; definition: string };
export type SectionLink = {
  sectionId: string;
  sectionName: string;
  firstPageId: string;
};
export type BatesLink = {
  batesNumber: string;
  pageId: string;
};
export type SectionReferenceScope = "page" | "section";
export type SectionClickHandler = (
  sectionId: string,
  pageId: string,
  context: string,
  scope: SectionReferenceScope
) => void;

/**
 * Convert literal \n sequences (backslash + n) in API responses
 * to actual newline characters for proper Markdown rendering.
 */
export const normalizeLiteralNewlines = (content: string): string => {
  if (!content) return content;
  return content.replace(/\\n/g, "\n");
};

const PIPE_CHAR_RE = /\|/;
const CARRIAGE_RETURN_RE = /\r\n?/g;
const TABLE_DELIMITER_CELL_RE = /^:?-*:?$/;
const TABLE_DELIMITER_CELL_HAS_CONTENT_RE = /[:\-]/;
const MAX_TABLE_INDENT_SPACES = 3;
const TABLE_PIPE_CHAR = "|";
const TABLE_ESCAPE_CHAR = "\\";
const BACKSLASH_CHAR = "\\";
const TABLE_INDENT_RE = /^[ \t]*/;
const BLOCKQUOTE_PREFIX_RE = /^([ \t]*>\s?)/;
const ORDERED_LIST_PREFIX_RE = /^([ \t]*\d{1,3}\.\s+)/;
const UNORDERED_LIST_PREFIX_RE = /^([ \t]*[-*+]\s+)/;
const TABLE_WRAPPER_STYLES: ReadonlyArray<{
  leading: boolean;
  trailing: boolean;
}> = [
  { leading: true, trailing: true },
  { leading: true, trailing: false },
  { leading: false, trailing: true },
  { leading: false, trailing: false },
];

type TableRowParts = {
  indentation: string;
  cells: string[];
  hasLeadingPipe: boolean;
  hasTrailingPipe: boolean;
};

type MarkdownLineParts = {
  prefix: string;
  content: string;
  hasContainerPrefix: boolean;
};

const EMPTY_LINE = "";
const TABLE_ROW_SEPARATOR = " | ";
const TABLE_ROW_OPEN = "| ";
const TABLE_ROW_CLOSE = " |";
const GLUED_TRAILING_PROSE_MIN_LENGTH = 40;
const GLUED_TRAILING_PROSE_WHITESPACE_RE = /\s/;

function splitOnUnescapedPipes(line: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let isEscaping = false;

  for (const char of line) {
    if (isEscaping) {
      cell += char;
      isEscaping = false;
      continue;
    }

    if (char === TABLE_ESCAPE_CHAR) {
      cell += char;
      isEscaping = true;
      continue;
    }

    if (char === TABLE_PIPE_CHAR) {
      cells.push(cell);
      cell = "";
      continue;
    }

    cell += char;
  }

  cells.push(cell);
  return cells;
}

function hasUnescapedTrailingPipe(line: string): boolean {
  if (!line.endsWith(TABLE_PIPE_CHAR)) return false;

  let backslashCount = 0;
  for (let i = line.length - 2; i >= 0; i--) {
    if (line[i] !== BACKSLASH_CHAR) break;
    backslashCount++;
  }

  return backslashCount % 2 === 0;
}

function splitMarkdownLinePrefix(line: string): MarkdownLineParts {
  const indentation = line.match(TABLE_INDENT_RE)?.[0] ?? "";
  const afterIndentation = line.slice(indentation.length);

  const blockquoteMatch = afterIndentation.match(BLOCKQUOTE_PREFIX_RE);
  if (blockquoteMatch) {
    const prefix = blockquoteMatch[1];
    return {
      prefix: `${indentation}${prefix}`,
      content: afterIndentation.slice(prefix.length),
      hasContainerPrefix: true,
    };
  }

  const orderedListMatch = afterIndentation.match(ORDERED_LIST_PREFIX_RE);
  if (orderedListMatch) {
    const prefix = orderedListMatch[1];
    return {
      prefix: `${indentation}${prefix}`,
      content: afterIndentation.slice(prefix.length),
      hasContainerPrefix: true,
    };
  }

  const unorderedListMatch = afterIndentation.match(UNORDERED_LIST_PREFIX_RE);
  if (unorderedListMatch) {
    const prefix = unorderedListMatch[1];
    return {
      prefix: `${indentation}${prefix}`,
      content: afterIndentation.slice(prefix.length),
      hasContainerPrefix: true,
    };
  }

  return {
    prefix: indentation,
    content: afterIndentation,
    hasContainerPrefix: false,
  };
}

function isIndentedCodeLine(line: string): boolean {
  const indentation = line.match(TABLE_INDENT_RE)?.[0] ?? "";
  return (
    indentation.includes("\t") || indentation.length > MAX_TABLE_INDENT_SPACES
  );
}

function splitTableRow(
  line: string,
  wrapperStyle: { leading: boolean; trailing: boolean }
): TableRowParts {
  const indentation = line.match(TABLE_INDENT_RE)?.[0] ?? "";
  const trimmedLine = line.slice(indentation.length).trimEnd();
  const hasLeadingPipe =
    wrapperStyle.leading && trimmedLine.startsWith(TABLE_PIPE_CHAR);
  const hasTrailingPipe =
    wrapperStyle.trailing && hasUnescapedTrailingPipe(trimmedLine);

  let innerLine = trimmedLine;
  if (hasLeadingPipe) {
    innerLine = innerLine.substring(1);
  }
  if (hasTrailingPipe) {
    innerLine = innerLine.substring(0, innerLine.length - 1);
  }

  return {
    indentation,
    cells: splitOnUnescapedPipes(innerLine).map((cell) => cell.trim()),
    hasLeadingPipe,
    hasTrailingPipe,
  };
}

function getTableRowCandidates(line: string): TableRowParts[] {
  const indentation = line.match(TABLE_INDENT_RE)?.[0] ?? "";
  const trimmedLine = line.slice(indentation.length).trimEnd();
  const hasLeadingPipe = trimmedLine.startsWith(TABLE_PIPE_CHAR);
  const hasTrailingPipe = hasUnescapedTrailingPipe(trimmedLine);

  return TABLE_WRAPPER_STYLES.filter(
    (style) =>
      (!style.leading || hasLeadingPipe) && (!style.trailing || hasTrailingPipe)
  ).map((style) => splitTableRow(line, style));
}

function isValidDelimiterRowParts(parts: TableRowParts): boolean {
  return (
    isDelimiterShapedRowParts(parts) &&
    parts.cells.every((cell) => cell.includes("-"))
  );
}

function isMalformedDelimiterRowParts(parts: TableRowParts): boolean {
  return (
    isDelimiterShapedRowParts(parts) &&
    !parts.cells.every((cell) => cell.includes("-"))
  );
}

function findMatchingDelimiterRow(
  line: string,
  expectedCellCount: number
): TableRowParts | null {
  for (const candidate of getTableRowCandidates(line)) {
    if (
      candidate.cells.length === expectedCellCount &&
      isMalformedDelimiterRowParts(candidate)
    ) {
      return candidate;
    }
  }

  return null;
}

function isTableBlockBoundaryLine(line: string | undefined): boolean {
  if (line === undefined) return true;

  const trimmedLine = line.trim();
  if (!trimmedLine) return true;
  if (!PIPE_CHAR_RE.test(trimmedLine)) return true;
  if (isIndentedCodeLine(line)) return true;

  return false;
}

function isDelimiterShapedRowParts(parts: TableRowParts): boolean {
  return (
    parts.cells.length > 0 &&
    parts.cells.every((cell) => TABLE_DELIMITER_CELL_RE.test(cell)) &&
    parts.cells.some((cell) => TABLE_DELIMITER_CELL_HAS_CONTENT_RE.test(cell))
  );
}

type RawHtmlBlockState = {
  inPre: boolean;
  inCode: boolean;
};

const RAW_HTML_PRE_OPEN_RE = /<pre\b[^>]*>/i;
const RAW_HTML_CODE_OPEN_RE = /<code\b[^>]*>/i;
const RAW_HTML_PRE_CLOSE_RE = /<\/pre>/i;
const RAW_HTML_CODE_CLOSE_RE = /<\/code>/i;

function hasRawHtmlBlockOpen(line: string): boolean {
  return RAW_HTML_PRE_OPEN_RE.test(line) || RAW_HTML_CODE_OPEN_RE.test(line);
}

function updateRawHtmlBlockState(
  line: string,
  currentState: RawHtmlBlockState
): RawHtmlBlockState {
  const nextState = { ...currentState };

  if (RAW_HTML_PRE_OPEN_RE.test(line)) {
    nextState.inPre = true;
  }
  if (RAW_HTML_CODE_OPEN_RE.test(line)) {
    nextState.inCode = true;
  }
  if (RAW_HTML_PRE_CLOSE_RE.test(line)) {
    nextState.inPre = false;
  }
  if (RAW_HTML_CODE_CLOSE_RE.test(line)) {
    nextState.inCode = false;
  }

  return nextState;
}

function getBestTableRowCandidate(line: string): TableRowParts | null {
  return getTableRowCandidates(line)[0] ?? null;
}

function buildTableRow(cells: string[]): string {
  return `${TABLE_ROW_OPEN}${cells.join(TABLE_ROW_SEPARATOR)}${TABLE_ROW_CLOSE}`;
}

function findUnescapedPipeIndex(
  line: string,
  pipeOrdinal: number
): number | null {
  if (pipeOrdinal < 1) return null;

  let isEscaping = false;
  let seenPipes = 0;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (isEscaping) {
      isEscaping = false;
      continue;
    }

    if (char === TABLE_ESCAPE_CHAR) {
      isEscaping = true;
      continue;
    }

    if (char === TABLE_PIPE_CHAR) {
      seenPipes++;
      if (seenPipes === pipeOrdinal) {
        return i;
      }
    }
  }

  return null;
}

function splitTableRowContentAtCellCount(
  line: string,
  parts: TableRowParts,
  cellCount: number
): { before: string; after: string } | null {
  if (parts.cells.length <= cellCount) return null;

  const indentation = line.match(TABLE_INDENT_RE)?.[0] ?? "";
  const trimmedLine = line.slice(indentation.length).trimEnd();
  let innerLine = trimmedLine;

  if (parts.hasLeadingPipe) {
    innerLine = innerLine.substring(1);
  }
  if (parts.hasTrailingPipe) {
    innerLine = innerLine.substring(0, innerLine.length - 1);
  }

  const boundaryIndex = findUnescapedPipeIndex(innerLine, cellCount);
  if (boundaryIndex === null) return null;

  return {
    before: innerLine.slice(0, boundaryIndex),
    after: innerLine.slice(boundaryIndex + 1),
  };
}

function isGluedTrailingProse(text: string): boolean {
  const trimmed = text.trim();
  return (
    findUnescapedPipeIndex(trimmed, 1) === null &&
    trimmed.length >= GLUED_TRAILING_PROSE_MIN_LENGTH &&
    GLUED_TRAILING_PROSE_WHITESPACE_RE.test(trimmed)
  );
}

export const unwrapAllCodeBlocks = (content: string): string => {
  if (!content) return content;

  const lines = content.split("\n");
  const out: string[] = [];

  let inFence = false;
  let fenceMarker = "";
  let blockStartIdx = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const fenceMatch = line.match(/^(\s*)(```|~~~)([a-z0-9-]*)\s*$/i);

    if (fenceMatch) {
      const marker = fenceMatch[2];
      if (!inFence) {
        inFence = true;
        fenceMarker = marker;
        blockStartIdx = out.length;
      } else if (marker === fenceMarker) {
        const blockLines = out.slice(blockStartIdx);
        const keptBefore = out.slice(0, blockStartIdx);
        out.length = 0;
        out.push(...keptBefore, ...blockLines);

        inFence = false;
        fenceMarker = "";
        blockStartIdx = -1;
      } else {
        out.push(line);
      }
      continue;
    }

    out.push(line);
  }

  return out.join("\n");
};

export const isolateGluedTableBlocks = (content: string): string => {
  if (!content || !PIPE_CHAR_RE.test(content)) return content;

  const lines = content.replace(CARRIAGE_RETURN_RE, "\n").split("\n");
  const normalizedLines: string[] = [];
  let rawHtmlBlockState: RawHtmlBlockState = {
    inPre: false,
    inCode: false,
  };
  let activeTableColumnCount: number | null = null;

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index];
    const lineParts = splitMarkdownLinePrefix(line);
    const shouldPreserveRawHtmlLine =
      rawHtmlBlockState.inPre ||
      rawHtmlBlockState.inCode ||
      hasRawHtmlBlockOpen(lineParts.content);

    rawHtmlBlockState = updateRawHtmlBlockState(
      lineParts.content,
      rawHtmlBlockState
    );

    if (shouldPreserveRawHtmlLine) {
      normalizedLines.push(line);
      activeTableColumnCount = null;
      continue;
    }

    if (lineParts.hasContainerPrefix) {
      normalizedLines.push(line);
      activeTableColumnCount = null;
      continue;
    }

    if (!PIPE_CHAR_RE.test(lineParts.content)) {
      normalizedLines.push(line);
      activeTableColumnCount = null;
      continue;
    }

    if (isIndentedCodeLine(line)) {
      normalizedLines.push(line);
      activeTableColumnCount = null;
      continue;
    }

    const bestCandidate = getBestTableRowCandidate(lineParts.content);
    if (!bestCandidate) {
      normalizedLines.push(line);
      continue;
    }

    if (activeTableColumnCount === null) {
      if (!isDelimiterShapedRowParts(bestCandidate)) {
        normalizedLines.push(line);
        continue;
      }

      const previousLine = lines[index - 1];
      if (!previousLine) {
        normalizedLines.push(line);
        continue;
      }

      const previousLineParts = splitMarkdownLinePrefix(previousLine);
      if (previousLineParts.hasContainerPrefix) {
        normalizedLines.push(line);
        continue;
      }

      if (isIndentedCodeLine(previousLine)) {
        normalizedLines.push(line);
        continue;
      }

      if (!PIPE_CHAR_RE.test(previousLineParts.content)) {
        normalizedLines.push(line);
        continue;
      }

      const previousPreviousLineParts = splitMarkdownLinePrefix(
        lines[index - 2] ?? EMPTY_LINE
      );
      if (!isTableBlockBoundaryLine(previousPreviousLineParts.content)) {
        normalizedLines.push(line);
        continue;
      }

      const headerRow = getBestTableRowCandidate(previousLineParts.content);
      if (!headerRow) {
        normalizedLines.push(line);
        continue;
      }

      const delimiterCellCount = bestCandidate.cells.length;
      if (headerRow.cells.length > delimiterCellCount) {
        const splitHeader = splitTableRowContentAtCellCount(
          previousLineParts.content,
          headerRow,
          headerRow.cells.length - delimiterCellCount
        );
        if (splitHeader) {
          normalizedLines.pop();
          normalizedLines.push(
            `${previousLineParts.prefix}${splitHeader.before}`,
            EMPTY_LINE,
            buildTableRow(headerRow.cells.slice(-delimiterCellCount))
          );
        }
      }

      normalizedLines.push(line);
      activeTableColumnCount = delimiterCellCount;
      continue;
    }

    if (bestCandidate.cells.length > activeTableColumnCount) {
      const splitBody = splitTableRowContentAtCellCount(
        lineParts.content,
        bestCandidate,
        activeTableColumnCount
      );
      if (splitBody && isGluedTrailingProse(splitBody.after)) {
        const overflowCells = bestCandidate.cells.slice(activeTableColumnCount);
        if (overflowCells.some((cell) => cell.trim().length > 0)) {
          normalizedLines.push(
            buildTableRow(bestCandidate.cells.slice(0, activeTableColumnCount)),
            EMPTY_LINE,
            `${lineParts.prefix}${splitBody.after}`
          );
          activeTableColumnCount = null;
          continue;
        }
      }
    }

    normalizedLines.push(line);
  }

  return normalizedLines.join("\n");
};

export const normalizeTableDelimiters = (content: string): string => {
  if (!content || !PIPE_CHAR_RE.test(content)) return content;

  const lines = content.replace(CARRIAGE_RETURN_RE, "\n").split("\n");
  let rawHtmlBlockState: RawHtmlBlockState = {
    inPre: false,
    inCode: false,
  };

  const normalizedLines = lines.map((line, index) => {
    const lineParts = splitMarkdownLinePrefix(line);
    const shouldPreserveRawHtmlLine =
      rawHtmlBlockState.inPre ||
      rawHtmlBlockState.inCode ||
      hasRawHtmlBlockOpen(lineParts.content);

    rawHtmlBlockState = updateRawHtmlBlockState(
      lineParts.content,
      rawHtmlBlockState
    );

    if (shouldPreserveRawHtmlLine) return line;

    if (!PIPE_CHAR_RE.test(lineParts.content)) return line;
    if (isIndentedCodeLine(line) && !lineParts.hasContainerPrefix) {
      return line;
    }

    const previousLine = lines[index - 1];
    if (!previousLine) return line;

    const previousLineParts = splitMarkdownLinePrefix(previousLine);
    if (
      isIndentedCodeLine(previousLine) &&
      !previousLineParts.hasContainerPrefix
    ) {
      return line;
    }

    if (!PIPE_CHAR_RE.test(previousLineParts.content)) return line;

    const previousPreviousLineParts = splitMarkdownLinePrefix(
      lines[index - 2] ?? ""
    );
    if (!isTableBlockBoundaryLine(previousPreviousLineParts.content)) {
      return line;
    }

    const previousTableRowCandidates = getTableRowCandidates(
      previousLineParts.content
    );
    const headerRow =
      previousTableRowCandidates.find(isValidDelimiterRowParts) ??
      previousTableRowCandidates[0];

    if (!headerRow) return line;
    if (isValidDelimiterRowParts(headerRow)) return line;

    const delimiterRow = findMatchingDelimiterRow(
      lineParts.content,
      headerRow.cells.length
    );
    if (!delimiterRow) return line;

    const { indentation, cells, hasLeadingPipe, hasTrailingPipe } =
      delimiterRow;

    if (cells.length === 0) return line;
    if (headerRow.cells.length !== cells.length) return line;

    const normalizedCells = cells.map((cell) => {
      if (cell.includes("-")) return cell;
      if (cell.startsWith(":") && cell.endsWith(":") && cell.length > 1) {
        return ":---:";
      }
      if (cell.endsWith(":") && cell.length > 1) {
        return "---:";
      }
      if (cell.startsWith(":")) {
        return ":---";
      }
      return "---";
    });

    const core = normalizedCells.join(" | ");
    const wrappedCore = `${hasLeadingPipe ? "| " : ""}${core}${hasTrailingPipe ? " |" : ""}`;

    return `${lineParts.prefix}${indentation}${wrappedCore}`;
  });

  return normalizedLines.join("\n");
};
/**
 * Ensure markdown block-level markers (list items and headings) that appear
 * mid-line get proper newlines inserted before them.
 */
export const ensureMarkdownNewlines = (content: string): string => {
  if (!content) return content;

  let result = content;

  // Insert newline before unordered list markers (- * +)
  result = result.replace(/([.!?:;,)]|(?<!\w)\])(?!\n) ?([-*+] )/g, "$1\n$2");

  // Insert newline before ordered list markers (1. 2. etc.) — limit to 1-3 digit numbers
  // to avoid matching years like "2019." as list markers
  result = result.replace(/([!?\]:;,)])(?!\n) ?(\d{1,3}\. )/g, "$1\n$2");
  result = result.replace(/(?<!\d)(\.)(?!\n) ?(\d{1,3}\. )/g, "$1\n$2");

  // After closing bold markers (**), insert newline before list markers
  result = result.replace(/(\*\*) ([-*+] )/g, "$1\n$2");
  result = result.replace(/(\*\*) (\d{1,3}\. )/g, "$1\n$2");

  result = result.replace(/([^\n]) +(#{1,6} )/g, "$1\n\n$2");

  return result;
};

export const ensureBoldHeadingSpacing = (content: string): string => {
  if (!content) return content;

  let result = content;

  result = result.replace(/(\*\*\w+(?:\s+\w+)+\*\*)([A-Z])/g, "$1\n\n$2");

  // The lookbehind suppresses the break only for an ordered-list marker — a
  // period preceded by 1-3 digits that run back to the start of a line (e.g.
  // `2.`) — so the marker is not orphaned from a following capitalized bold
  // span (`2. **Undated**`). The digit cap mirrors the 1-3 digit marker rule
  // in `ensureMarkdownNewlines` above so a 4-digit year (`2024.`) still splits.
  // The guard is scoped to `.` only: `!`/`?` are never list markers, and a
  // sentence merely ending in a number (`June 1.`) is not at a line start, so
  // both still split as intended.
  result = result.replace(
    /((?<!(?:^|\n)[ \t]*\d{1,3})\.|[!?])[ \t]*(?:\n(?!\n))?[ \t]*((?:-\s*)?\*\*[A-Z][^*]*\*\*)/g,
    "$1\n\n$2"
  );

  result = result.replace(/\n{3,}/g, "\n\n");

  return result;
};

// `prose-ol:ps-10` widens the ordered-list marker gutter so 3+ digit markers
// (e.g. `100.`) are not clipped by the `prose-sm` default `padding-inline-start`.
// See SKY-2373 — the "APPENDIX A: DOCUMENT LIST" in report view renders via
// UnifiedMarkdown and needs to accommodate lists with more than 99 items.
export const TERMS_PROSE_CLASS =
  "prose prose-sm dark:prose-invert font-sans normal-case prose-ol:ps-10";

// Reference context is fed to the context-mode bounding-box endpoint, whose
// query length is capped at 1000 characters.
const MAX_REFERENCE_CONTEXT_LENGTH = 1000;

const normalizeWhitespace = (value: string): string =>
  value.replace(/\s+/g, " ");

const CITATION_LINK_SELECTOR =
  "a.source-map-link, a.bates-link, a.section-link";

// Index just past the last sentence-ending punctuation (".", "!", "?"), i.e.
// the start of the final sentence in `text`. Returns 0 when there is none.
function lastSentenceStart(text: string): number {
  const boundary = /[.!?]\s+/g;
  let start = 0;
  let match: RegExpExecArray | null;
  while ((match = boundary.exec(text)) !== null) {
    start = match.index + match[0].length;
  }
  return start;
}

/**
 * Collect the block's text on either side of the clicked link, omitting the
 * label text of the clicked link and of any sibling citation links. Reading
 * from the DOM (rather than string-searching the label) anchors on the element
 * the user actually clicked, and dropping neighbouring citation labels keeps
 * markers like "[1]" from masking sentence boundaries between citations.
 */
function splitTextAroundLink(
  block: HTMLElement,
  link: HTMLElement
): { before: string; after: string } {
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  let before = "";
  let after = "";
  let node = walker.nextNode();
  while (node) {
    const parent = node.parentElement;
    const isCitationLabel =
      link.contains(node) || !!parent?.closest(CITATION_LINK_SELECTOR);
    if (!isCitationLabel) {
      const relation = link.compareDocumentPosition(node);
      if (relation & Node.DOCUMENT_POSITION_FOLLOWING) {
        after += node.textContent ?? "";
      } else {
        before += node.textContent ?? "";
      }
    }
    node = walker.nextNode();
  }
  return { before, after };
}

/**
 * Extract the sentence/idea surrounding a clicked reference link so it can be
 * used to highlight the relevant region of the source page (context heatmap).
 * Falls back to the enclosing block's text when a single sentence can't be
 * isolated.
 */
export function extractReferenceContext(link: HTMLElement): string {
  // Citations collapsed into a dropdown are portaled out of their paragraph, so
  // their enclosing block no longer carries the surrounding prose. The dropdown
  // precomputes the context from the trigger's in-paragraph position and exposes
  // it here via data-reference-context.
  const precomputed = link.closest<HTMLElement>("[data-reference-context]");
  if (precomputed) {
    return precomputed.dataset.referenceContext ?? "";
  }

  const block =
    link.closest<HTMLElement>(
      "p, li, td, th, blockquote, h1, h2, h3, h4, h5, h6"
    ) ?? link.parentElement;
  if (!block) {
    return normalizeWhitespace(link.textContent ?? "")
      .trim()
      .slice(0, MAX_REFERENCE_CONTEXT_LENGTH);
  }

  const { before: rawBefore, after: rawAfter } = splitTextAroundLink(
    block,
    link
  );
  const before = normalizeWhitespace(rawBefore).replace(/\s+$/, "");
  const after = normalizeWhitespace(rawAfter).trim();
  const fullText = normalizeWhitespace(`${rawBefore} ${rawAfter}`).trim();
  if (!fullText) return "";

  // Citation attached to the end of its sentence (e.g. "...no complaints.[1]").
  if (/[.!?]$/.test(before)) {
    const sentence = before.slice(lastSentenceStart(before.slice(0, -1)));
    return (sentence.trim() || fullText).slice(0, MAX_REFERENCE_CONTEXT_LENGTH);
  }

  // Citation embedded mid-sentence: take from the previous boundary in `before`
  // through the next boundary in `after`.
  const head = before.slice(lastSentenceStart(before));
  const afterMatch = after.search(/[.!?](\s|$)/);
  const tail = afterMatch >= 0 ? after.slice(0, afterMatch + 1) : after;
  const sentence = normalizeWhitespace(`${head} ${tail}`).trim();

  return (sentence || fullText).slice(0, MAX_REFERENCE_CONTEXT_LENGTH);
}

/**
 * Event-delegation hook for source-map and bates-link clicks.
 *
 * The optional `context` argument passed to the handlers carries the
 * sentence/idea surrounding the clicked reference, so callers can highlight the
 * relevant area of the source page (see `extractReferenceContext`).
 */
export function useSourceMapDelegation(
  el: HTMLElement | null,
  onSectionClick?: SectionClickHandler,
  onBatesClick?: (pageId: string, context?: string) => void
) {
  React.useEffect(() => {
    if (!el) return;

    const handler = (e: MouseEvent) => {
      const target = e.target as Element;

      const sourceMapLink = target.closest<HTMLElement>("a.source-map-link");
      if (sourceMapLink && el.contains(sourceMapLink)) {
        e.preventDefault();
        trackEvent({
          eventName: "app/chat/source_citation.link/click",
          properties: { hasBatesNumber: false, sourceType: "section" },
        });
        const sectionId = sourceMapLink.getAttribute("data-section-id") ?? "";
        const explicitPageId = sourceMapLink.getAttribute("data-page-id") ?? "";
        const firstPageId =
          sourceMapLink.getAttribute("data-first-page-id") ?? "";
        const pageId = explicitPageId || firstPageId;
        if (sectionId || pageId)
          onSectionClick?.(
            sectionId,
            pageId,
            extractReferenceContext(sourceMapLink),
            explicitPageId ? "page" : "section"
          );
        return;
      }

      const batesLink = target.closest<HTMLElement>("a.bates-link");
      if (batesLink && el.contains(batesLink)) {
        e.preventDefault();
        trackEvent({
          eventName: "app/chat/source_citation.link/click",
          properties: { hasBatesNumber: true, sourceType: "bates" },
        });
        const pageId = batesLink.getAttribute("data-bates-page-id");
        if (pageId) onBatesClick?.(pageId, extractReferenceContext(batesLink));
        return;
      }

      const sectionLink = target.closest<HTMLElement>("a.section-link");
      if (sectionLink && el.contains(sectionLink)) {
        e.preventDefault();
        trackEvent({
          eventName: "app/chat/source_citation.link/click",
          properties: { hasBatesNumber: false, sourceType: "section" },
        });
        const sectionId = sectionLink.getAttribute("data-section-id") ?? "";
        const explicitPageId = sectionLink.getAttribute("data-page-id") ?? "";
        const firstPageId =
          sectionLink.getAttribute("data-first-page-id") ?? "";
        const pageId = explicitPageId || firstPageId;
        if (sectionId || pageId)
          onSectionClick?.(
            sectionId,
            pageId,
            extractReferenceContext(sectionLink),
            explicitPageId ? "page" : "section"
          );
        return;
      }

      // Structured data accordion toggle
      const trailerHeader = target.closest<HTMLElement>(
        ".structured-data-trailer__header"
      );
      if (trailerHeader && el.contains(trailerHeader)) {
        const trailer = trailerHeader.closest(".structured-data-trailer");
        if (trailer) {
          trailer.classList.toggle("open");
        }
        return;
      }
    };

    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [el, onSectionClick, onBatesClick]);
}

/**
 * Remove `<a>...</a>` tags from text, keeping only the inner text content.
 * Strip standalone HTML tags (ul, ol, li, b, strong, i, em) while preserving their content.
 * Also convert literal \n sequences to actual newlines for proper Markdown rendering.
 */
export const stripAnchorTagsAndNormalizeNewlines = (
  content: string
): string => {
  if (!content) return content;
  return content
    .replace(/<a\b[^>]*>.*?<\/a>/gi, "")
    .replace(/<\/?(ul|ol)\b[^>]*>/gi, "")
    .replace(/<li\b[^>]*>/gi, "* ")
    .replace(/<\/li>/gi, "\n")
    .replace(/<\/?(b|strong|i|em)\b[^>]*>/gi, "")
    .replace(/\\n/g, "\n");
};
